import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiURl from "../Paths/Api";

// SVG Icons as React components
const SearchIcon = () => (
  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const StarBorder = ({
  as: Component = 'button',
  className = '',
  color = '#FFD700',
  speed = '3s',
  thickness = 1.5,
  children,
  ...rest
}) => {
  return (
    <Component
      className={`relative inline-block overflow-hidden rounded-full ${className} hover:scale-105 transition-transform duration-200`}
      style={{ padding: `${thickness}px 0`, ...rest.style }}
      {...rest}
    >
      <div
        className="absolute w-[300%] h-[50%] opacity-50 bottom-[-10px] right-[-250%] rounded-full animate-star-movement z-0"
        style={{ background: `radial-gradient(circle, ${color}, transparent 20%)`, animationDuration: speed }}
      ></div>
      <div
        className="absolute w-[300%] h-[50%] opacity-50 top-[-10px] left-[-250%] rounded-full animate-star-movement z-0"
        style={{ background: `radial-gradient(circle, ${color}, transparent 20%)`, animationDuration: speed }}
      ></div>
      <div className="relative z-1 bg-gradient-to-r from-teal-600 to-cyan-600 border border-teal-700 text-white text-sm font-medium py-2 px-4 rounded-full shadow-sm hover:shadow-md transition-shadow duration-200">
        {children}
      </div>
    </Component>
  );
};

const AdminSendInvite = () => {
  const [meetings, setMeetings] = useState([]);
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);
  const [paymentId, setPaymentId] = useState('');
  const [orderId, setOrderId] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [pincode, setPincode] = useState('');
  const [address, setAddress] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [numTickets, setNumTickets] = useState('');
  const [success, setSuccess] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPublicMeetings();
  }, []);

  const fetchPublicMeetings = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${ApiURl}/public-meetings.php`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      const data = await response.json();
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Meetings API Response`, data);
      if (data.success && Array.isArray(data.meetings)) {
        const formattedMeetings = data.meetings.filter((meeting) => meeting.is_public == 1);
        setMeetings(formattedMeetings);
        if (formattedMeetings.length > 0 && !selectedMeetingId) {
          setSelectedMeetingId(formattedMeetings[0].id.toString());
        }
      } else {
        setWarnings([data.message || 'No events available.']);
      }
    } catch (err) {
      setWarnings(['Failed to fetch events.']);
      console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Meetings API Error`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const validateInputs = () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim().replace(/^\+?91/, '');
    const trimmedPincode = pincode.trim();
    const trimmedAddress = address.trim();
    const trimmedCompanyName = companyName.trim();
    const ticketCount = parseInt(numTickets) || 0;

    if (!trimmedName || !trimmedEmail || !trimmedPhone || !trimmedPincode || !trimmedAddress || !numTickets) {
      return 'All required fields are mandatory.';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      return 'Invalid email address.';
    }
    if (!/^\d{10}$/.test(trimmedPhone)) {
      return 'Invalid 10-digit phone number.';
    }
    if (!/^\d{6}$/.test(trimmedPincode)) {
      return 'Invalid 6-digit pincode.';
    }
    if (trimmedCompanyName && trimmedCompanyName.length > 255) {
      return 'Company name too long.';
    }
    if (ticketCount < 1) {
      return 'At least 1 ticket required.';
    }
    return '';
  };

  const handleSendInvite = async () => {
    if (!selectedMeetingId) {
      setWarnings(['Select an event.']);
      return;
    }
    const validationError = validateInputs();
    if (validationError) {
      setWarnings([validationError]);
      return;
    }
    const selectedMeeting = meetings.find((m) => m.id.toString() === selectedMeetingId);
    if (!selectedMeeting) {
      setWarnings(['Event unavailable.']);
      return;
    }
    const currentAttendeeCount = selectedMeeting.attendee_count ?? (selectedMeeting.join_requests?.length || 0);
    const availableSpots = selectedMeeting.capacity - currentAttendeeCount;
    const ticketCount = parseInt(numTickets) || 0;

    if (availableSpots < ticketCount) {
      setWarnings([`Only ${availableSpots} ticket(s) available.`]);
      return;
    }

    setIsLoading(true);
    try {
      const joinData = {
        action: 'send_invite',
        meetingId: selectedMeetingId,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim().startsWith('+91') ? phone.trim() : '+91' + phone.trim().replace(/^\+?91/, ''),
        pincode: pincode.trim(),
        address: address.trim(),
        companyName: companyName.trim(),
        numTickets: ticketCount,
        paymentId: paymentId || null,
        orderId: orderId || null,
      };
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Sending Invite`, joinData);
      const response = await fetch(`${ApiURl}/send-invite.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(joinData),
      });
      const data = await response.json();
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Invite Response`, data);
      if (data.success) {
        setSuccess('Invites sent successfully! Check email and WhatsApp.');
        setWarnings(data.warnings || []);
        setTimeout(() => {
          setSuccess('');
          setWarnings([]);
          setName('');
          setEmail('');
          setPhone('');
          setPincode('');
          setAddress('');
          setCompanyName('');
          setNumTickets('');
          setPaymentId('');
          setOrderId('');
          fetchPublicMeetings();
        }, 3000);
      } else {
        setWarnings([data.message || 'Failed to send invites.']);
      }
    } catch (err) {
      setWarnings(['Unexpected error. Try again.']);
      console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Invite Error`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const selectedMeeting = selectedMeetingId ? meetings.find((m) => m.id.toString() === selectedMeetingId) : null;
  const availableSpots = selectedMeeting ? selectedMeeting.capacity - (selectedMeeting.attendee_count ?? (selectedMeeting.join_requests?.length || 0)) : 0;

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-gray-50 to-gray-100'} font-sans`}>
      {/* Navbar */}
      <nav className="bg-gradient-to-r from-teal-600 to-cyan-600 dark:from-teal-800 dark:to-cyan-800 text-white p-4 shadow-xl fixed w-full z-20">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="mr-4 md:hidden focus:outline-none hover:bg-teal-700 dark:hover:bg-teal-900 p-2 rounded-full transition-all duration-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <h1 className="text-2xl font-bold tracking-tight">Event Manager</h1>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="p-2 rounded-full hover:bg-teal-700 dark:hover:bg-teal-900 transition-all duration-300"
            >
              {isDarkMode ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-red-500 rounded-full hover:bg-red-600 dark:hover:bg-red-700 transition-all duration-300 text-sm font-medium shadow-md hover:scale-105"
              disabled={isLoading}
            >
              Home
            </button>
          </div>
        </div>
      </nav>

      {/* Sidebar */}
      <div
        className={`fixed top-14 left-0 h-[calc(100vh-3.5rem)] bg-gradient-to-b from-teal-600 to-teal-800 text-white w-16 flex flex-col items-center py-4 space-y-4 transition-transform duration-300 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:w-16 z-20`}
      >
        <button
          className="p-2 rounded-full bg-teal-700 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Dashboard"
          onClick={() => navigate('/dashboard')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Dashboard</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Create Meeting"
          onClick={() => navigate('/create-meeting')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Create Meeting</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="View Meetings"
          onClick={() => navigate('/view-meetings')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Reports</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Send Invites"
          onClick={() => navigate('/send-invites')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7-4 7 4v8l-7 4-7-4V8zm0 0l7 4 7-4m-14 0v8l7 4 7-4V8" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Invite</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Search Meetings"
          onClick={() => navigate('/search-form')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Search</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Send Invite Without Pay"
          onClick={() => navigate('/adminsend')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7-4 7 4v8l-7 4-7-4V8zm0 0l7 4 7-4m-14 0v8l7 4 7-4V8" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Send Invite</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Restrict Meeting"
          onClick={() => navigate('/restrict-meeting')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 1.104-.896 2-2 2s-2-.896-2-2 2-4 2-4 2 2.896 2 4zm0 0c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4zm-6 7c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4zm12 0c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4z" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Restrict Meeting</span>
        </button>
      </div>

      {/* Main Content */}
      <main className="pt-20 md:pl-24 p-6 max-w-7xl mx-auto transition-all duration-500">
        {isLoading && (
          <div className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-xl">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500 mx-auto mb-2"></div>
              <p className="text-teal-700 dark:text-teal-300 text-sm font-medium">Processing...</p>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-semibold text-gray-900 dark:text-white animate-fade-in">Send Invites</h1>
          <button
            onClick={fetchPublicMeetings}
            className="px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 dark:hover:bg-teal-800 transition-all duration-300 text-sm font-medium shadow-lg hover:shadow-xl hover:scale-105"
            disabled={isLoading}
          >
            Refresh
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl animate-fade-in border border-gray-200 dark:border-gray-700 p-6">
          {success && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-xl border border-green-200 dark:border-green-700 text-sm animate-slide-in flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {success}
            </div>
          )}

          {warnings.length > 0 && (
            <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-xl border border-yellow-200 dark:border-yellow-700 text-sm animate-slide-in">
              <div className="flex items-center mb-2">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="font-semibold">Warning</span>
              </div>
              <ul className="list-disc pl-6 text-sm">
                {warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-200 mb-2">Select Event</label>
              <div className="relative">
                <select
                  value={selectedMeetingId || ''}
                  onChange={(e) => setSelectedMeetingId(e.target.value)}
                  className="w-full pl-12 pr-8 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:border-teal-400 dark:text-white appearance-none shadow-sm"
                  disabled={isLoading}
                >
                  <option value="">Select an event</option>
                  {meetings.map((meeting) => (
                    <option key={meeting.id} value={meeting.id}>
                      {(meeting.title || 'Untitled Event')} - {meeting.company_name || 'Unknown'}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center">
                  <SearchIcon />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Payment Details (Optional)</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-200 mb-2">Payment ID</label>
                  <input
                    type="text"
                    value={paymentId}
                    onChange={(e) => setPaymentId(e.target.value)}
                    placeholder="Payment ID (optional)"
                    className="w-full px-3 py-3 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-gray-50 dark:bg-gray-700 dark:text-white shadow-sm transition-all duration-300 hover:border-teal-400"
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-200 mb-2">Order ID</label>
                  <input
                    type="text"
                    value={orderId}
                    onChange={(e) => setOrderId(e.target.value)}
                    placeholder="Order ID (optional)"
                    className="w-full px-3 py-3 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-gray-50 dark:bg-gray-700 dark:text-white shadow-sm transition-all duration-300 hover:border-teal-400"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Attendee Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-200 mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Name"
                    className="w-full px-3 py-3 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-gray-50 dark:bg-gray-700 dark:text-white shadow-sm transition-all duration-300 hover:border-teal-400"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-200 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    className="w-full px-3 py-3 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-gray-50 dark:bg-gray-700 dark:text-white shadow-sm transition-all duration-300 hover:border-teal-400"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-200 mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Phone"
                    className="w-full px-3 py-3 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-gray-50 dark:bg-gray-700 dark:text-white shadow-sm transition-all duration-300 hover:border-teal-400"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-200 mb-2">
                    Pincode <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value)}
                    placeholder="Pincode"
                    className="w-full px-3 py-3 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-gray-50 dark:bg-gray-700 dark:text-white shadow-sm transition-all duration-300 hover:border-teal-400"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-200 mb-2">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Address"
                    className="w-full px-3 py-3 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-gray-50 dark:bg-gray-700 dark:text-white shadow-sm transition-all duration-300 hover:border-teal-400"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-200 mb-2">Company</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Company (optional)"
                    className="w-full px-3 py-3 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-gray-50 dark:bg-gray-700 dark:text-white shadow-sm transition-all duration-300 hover:border-teal-400"
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-200 mb-2">
                    Tickets <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={availableSpots}
                    value={numTickets}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNumTickets(value === '' ? '' : Math.min(availableSpots, parseInt(value) || 1).toString());
                    }}
                    placeholder="Tickets"
                    className="w-full px-3 py-3 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-gray-50 dark:bg-gray-700 dark:text-white shadow-sm transition-all duration-300 hover:border-teal-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    required
                    disabled={isLoading}
                  />
                  <p className="text-sm text-teal-600 dark:text-teal-400 mt-2">{availableSpots} available</p>
                </div>
                <div className="flex items-end">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    Total: ₹{selectedMeeting ? selectedMeeting.price * (parseInt(numTickets) || 0) : 0}
                  </p>
                </div>
              </div>
              <StarBorder
                className="w-full mt-4 text-sm"
                color="#FFD700"
                speed="2s"
                thickness={1}
                onClick={handleSendInvite}
                disabled={isLoading || availableSpots < 1}
              >
                {isLoading ? 'Processing...' : availableSpots < 1 ? 'Full' : 'Send Invites'}
              </StarBorder>
            </div>
          </div>
        </div>
      </main>

      {/* Tailwind Animation Styles */}
      <style jsx>{`
        @keyframes star-movement {
          0% { transform: translate(0%, 0%); opacity: 0.6; }
          100% { transform: translate(-100%, 0%); opacity: 0; }
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-in {
          from { opacity: 0; transform: translateX(-10px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-star-movement {
          animation: star-movement linear infinite alternate;
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
        .animate-slide-in {
          animation: slide-in 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AdminSendInvite;